// src/services/paymentService.js - Aktualisiert für MongoDB API
import api from './api';

// Preise für die verschiedenen Abonnements
export const PLAN_PRICES = {
    basic: {
        monthly: 999, // 9.99€ in Cent
        yearly: 9999  // 99.99€ in Cent
    },
    premium: {
        monthly: 1999, // 19.99€ in Cent
        yearly: 19999  // 199.99€ in Cent
    },
    professional: {
        monthly: 2999, // 29.99€ in Cent
        yearly: 29999  // 299.99€ in Cent
    }
};

// Stripe importieren (wenn Stripe.js bereits im HTML eingebunden ist)
let stripePromise;
try {
    stripePromise = import('@stripe/stripe-js').then(module =>
        module.loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
    );
} catch (error) {
    console.warn('Stripe konnte nicht geladen werden:', error);
}

/**
 * Checkout-Session für ein Abonnement erstellen
 * @param {string} shopId - Shop-ID
 * @param {string} plan - Abonnementplan
 * @param {string} interval - Zahlungsintervall
 * @returns {Promise<Object>} - Ergebnis der Checkout-Session-Erstellung
 */
export const createCheckoutSession = async (shopId, plan, interval) => {
    try {
        // Prüfen, ob die Parameter gültig sind
        if (!shopId || !plan || !interval) {
            throw new Error('Shop-ID, Plan und Zahlungsintervall sind erforderlich');
        }

        // Prüfen, ob der Plan und das Intervall gültig sind
        if (!PLAN_PRICES[plan] || !PLAN_PRICES[plan][interval]) {
            throw new Error('Ungültiger Plan oder Zahlungsintervall');
        }

        // API-Anfrage zur Erstellung einer Checkout-Session
        const response = await api.post('/payments/create-checkout-session', {
            shopId,
            plan,
            interval
        });

        if (!response.success) {
            throw new Error(response.message || 'Fehler beim Erstellen der Checkout-Session');
        }

        // Stripe Checkout initiieren
        if (!stripePromise) {
            throw new Error('Stripe ist nicht verfügbar');
        }

        const stripe = await stripePromise;
        if (!stripe) {
            throw new Error('Stripe konnte nicht initialisiert werden');
        }

        const { error } = await stripe.redirectToCheckout({
            sessionId: response.sessionId
        });

        if (error) {
            throw error;
        }

        return { success: true };
    } catch (error) {
        console.error('Checkout-Fehler:', error);
        return {
            success: false,
            message: error.message || 'Fehler bei der Zahlungsabwicklung'
        };
    }
};

/**
 * Abonnement kündigen
 * @param {string} shopId - Shop-ID
 * @returns {Promise<Object>} - Ergebnis der Kündigung
 */
export const cancelSubscription = async (shopId) => {
    try {
        if (!shopId) {
            throw new Error('Shop-ID ist erforderlich');
        }

        // API-Anfrage zum Kündigen des Abonnements
        const response = await api.post('/payments/cancel-subscription', { shopId });

        if (!response.success) {
            throw new Error(response.message || 'Fehler beim Kündigen des Abonnements');
        }

        return response;
    } catch (error) {
        console.error('Fehler beim Kündigen des Abonnements:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Kündigen des Abonnements'
        };
    }
};

/**
 * Zahlungshistorie abrufen
 * @param {string} shopId - Shop-ID (optional)
 * @returns {Promise<Object>} - Zahlungshistorie
 */
export const getPaymentHistory = async (shopId = null) => {
    try {
        // API-Parameter vorbereiten
        const params = shopId ? { shopId } : {};

        // API-Anfrage zur Abrufung der Zahlungshistorie
        const response = await api.get('/payments/history', params);

        if (!response.success) {
            throw new Error(response.message || 'Fehler beim Abrufen der Zahlungshistorie');
        }

        return response;
    } catch (error) {
        console.error('Fehler beim Abrufen der Zahlungshistorie:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Abrufen der Zahlungshistorie',
            data: []
        };
    }
};

/**
 * Verfügbare Pläne mit Preisen und Funktionen (nur bezahlte Pläne)
 * @returns {Array} - Array mit Planinformationen
 */
export const getPlans = () => {
    return [
        // Basic Plan entfernt
        /* {
            id: 'basic',
            name: 'Basic',
            price: { monthly: 9.99, yearly: 99.99 },
            features: [ ... ],
            recommended: false,
        }, */
        {
            id: 'premium',
            name: 'Premium',
            price: { 
                monthly: 19.99, // Preis angepasst
                yearly: 199.99  // Preis ggf. auch anpassen?
            },
            features: [
                'Hervorgehobene Shoplistung',
                'Erweiterte Profilseite',
                'Bevorzugte Platzierung',
                'Bis zu 10 Bilder',
                'Verifizierter Badge',
                'Markenfilterung',
                'Statistiken & Berichte',
            ],
            recommended: true,
        },
        {
            id: 'professional',
            name: 'Professional',
            price: { 
                monthly: 29.99, // Preis angepasst
                yearly: 299.99  // Preis ggf. auch anpassen?
            },
            features: [
                'Alle Premium-Funktionen',
                'Top-Platzierung',
                'Bis zu 20 Bilder',
                'Erweitertes Markenverzeichnis',
                'Exklusive Werbemöglichkeiten',
                'Premium Support',
            ],
            recommended: false,
        },
    ];
};

/**
 * Reaktiviert ein Abonnement, das zur Kündigung vorgemerkt war.
 * @param {string} shopId - Shop-ID
 * @returns {Promise<Object>} - Ergebnis der Reaktivierung
 */
export const reactivateSubscription = async (shopId) => {
    try {
        if (!shopId) {
            throw new Error('Shop-ID ist für die Reaktivierung erforderlich');
        }

        // API-Anfrage zum Reaktivieren des Abonnements
        const response = await api.post('/payments/reactivate-subscription', { shopId });

        if (!response.success) {
            throw new Error(response.message || 'Fehler beim Reaktivieren des Abonnements');
        }

        return response; // Enthält { success: true, message: '...' }
    } catch (error) {
        console.error('Fehler beim Reaktivieren des Abonnements im Service:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Reaktivieren des Abonnements'
        };
    }
};

/**
 * Abonnement sofort kündigen (nur wenn bereits zur Kündigung vorgemerkt)
 */
export const immediateCancelSubscription = async (shopId) => {
    // Implementierung der Methode
};